import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";

// "As Featured In" — pulls real press items from /api/press and renders as
// an editorial press strip. Logos are wordmarks set in serif to stay on-brand
// without needing third-party logo files.
export default function PressStrip() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/press").then((r) => setItems((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section className="relative border-y border-primary/10 bg-card/40 py-16 lg:py-20 px-6 lg:px-12" data-testid="press-strip">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-3">— Press</div>
            <h3 className="font-serif text-2xl md:text-4xl font-light tracking-tight leading-tight">
              As featured in
            </h3>
          </div>
          <Link
            to="/media"
            data-testid="press-strip-cta"
            className="group inline-flex items-center gap-2 text-primary text-[12px] uppercase tracking-[0.22em] hover:underline"
          >
            View all coverage <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-primary/10 border-t border-primary/10 border-b">
          {items.map((p, i) => (
            <motion.a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`press-strip-item-${p.id}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group flex flex-col justify-between gap-4 px-6 py-10 hover:bg-background/40 transition-colors"
            >
              <div className="font-serif text-lg md:text-2xl tracking-tight leading-tight text-foreground/90 group-hover:text-primary transition-colors text-balance">
                {p.outlet}
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/40">
                  {new Date(p.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                </div>
                <ArrowUpRight className="w-4 h-4 text-primary/60 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
